import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '../Tooltip/Tooltip';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Badge from '../Badge/Badge';
import { selectOptionClasses as classes } from './selectOptionClasses';
import {
  BadgeContainer,
  GroupContainer,
  IconContainer,
  LabelContainer,
  SecondaryTextContainer,
  SelectOptionRoot
} from './SelectOption.style';

const Arrow = ({ isGroupCollapsed, ...rest }) => {
  const Icon = isGroupCollapsed ? ExpandMoreIcon : ExpandLessIcon;
  return <Icon {...rest} />;
};

const SelectOption = forwardRef(
  (
    {
      label,
      secondaryText,
      renderIcon,
      value,
      BadgeProps,
      actionProps,
      CheckboxProps,
      isCheckbox,
      isIndeterminate,
      isSelected,
      isDisabled,
      isFocused,
      isGroup,
      isNested,
      items,
      isGroupCollapsed,
      groupName,
      isLastSelected,
      isPreSelectedItem,
      onClick,
      showGroupCounter: incomingShowGroupCounter = true,
      className: incomingClassName,
      ...rest
    },
    ref
  ) => {
    const abortEvent = e => {
      e.preventDefault();
      e.stopPropagation();
    };

    const {
      onClick: incomingOnCheckboxClick,
      isDisabled: incomingIsCheckboxDisabled,
      ...RestCheckboxProps
    } = CheckboxProps || {};

    const {
      onClick: incomingOnActionClick,
      icon: incomingActionIcon,
      name: incomingActionName,
      ...restActionProps
    } = actionProps || {};

    const isOptionDisabled = incomingIsCheckboxDisabled
      ? // priority for checkbox prop as it's a special case, for example - group category option
        incomingIsCheckboxDisabled
      : // default back to the disabled value provided for each option
        isDisabled;

    const onCheckboxClick = incomingOnCheckboxClick
      ? incomingOnCheckboxClick
      : onClick;

    const hasAction = incomingOnActionClick !== undefined;
    const showGroupCounter = isGroup && incomingShowGroupCounter;

    return (
      <SelectOptionRoot
        ref={ref}
        className={clsx(classes.root, incomingClassName, {
          [classes.selected]: isSelected && !isCheckbox,
          [classes.nested]: isNested,
          [classes.lastSelected]: isLastSelected,
          [classes.group]: isGroup,
          [classes.focused]: isFocused,
          [classes.disabled]: isOptionDisabled
        })}
        selected={isSelected && !isCheckbox}
        nested={isNested}
        lastSelected={isLastSelected}
        group={isGroup}
        focused={isFocused}
        disabled={isOptionDisabled}
        withCheckbox={isCheckbox}
        role="button"
        tabIndex={-1}
        data-qa-value={value}
        data-value={value}
        data-disabled={isDisabled}
        data-qa-disabled={isDisabled}
        data-qa-id="select-option"
        data-qa-focused={isFocused}
        data-qa-selected={isSelected}
        data-qa-group={isGroup}
        onKeyDown={e => {
          if (isDisabled) {
            abortEvent(e);
          }
        }}
        onClick={e => {
          if (isDisabled) {
            abortEvent(e);
          }

          onClick && onClick(e);
        }}
        {...rest}>
        {isCheckbox && (
          <IconContainer className={classes.icon}>
            {/*In the current implementation we get onClick from react-select with ...rest*/}
            <Checkbox
              tabIndex={-1}
              checked={isSelected}
              indeterminate={isIndeterminate}
              onClick={e => {
                if (onCheckboxClick) {
                  onCheckboxClick(e);

                  // We don't won't that both onCheckboxClick event and onCLick events would occur
                  // at the same time cause it could lead into some undesired behavior (check item
                  // and immediately uncheck it)
                  abortEvent(e);
                }
              }}
              inputProps={{
                'aria-label': label,
                'data-qa-id': 'checkbox'
              }}
              disabled={isOptionDisabled}
              {...RestCheckboxProps}
            />
          </IconContainer>
        )}
        {renderIcon && (
          <IconContainer className={classes.icon}>
            {renderIcon({
              size: 'medium'
            })}
          </IconContainer>
        )}
        <LabelContainer
          aria-label={label}
          role="button"
          hasAction={hasAction}
          data-qa-id="select-option-value"
          className={clsx(classes.text, {
            [classes.multiline]: secondaryText,
            [classes.groupText]: isGroup
          })}
          id={value}
          multiline={secondaryText}
          group={isGroup}
          isOptionDisabled={isOptionDisabled}>
          {isPreSelectedItem && groupName ? (
            <>
              {label},{' '}
              <GroupContainer className={classes.groupName}>
                {' '}
                {groupName}{' '}
              </GroupContainer>{' '}
            </>
          ) : (
            <>
              {label}
              {showGroupCounter && ` (${items.length})`}
              {isGroup && <Arrow isGroupCollapsed={isGroupCollapsed} />}
            </>
          )}
          {secondaryText && (
            <SecondaryTextContainer className={classes.secondaryText}>
              {secondaryText}
            </SecondaryTextContainer>
          )}
          {BadgeProps && (
            <BadgeContainer
              className={classes.badgeWrapper}
              data-qa-id="select-option-badge">
              <Badge {...BadgeProps} />
            </BadgeContainer>
          )}
        </LabelContainer>
        {incomingOnActionClick && (
          <Tooltip title={incomingActionName}>
            <IconButton
              data-qa-id="selectOption--action"
              onClick={incomingOnActionClick}
              aria-label={incomingActionName}
              size="small"
              {...restActionProps}>
              {incomingActionIcon}
            </IconButton>
          </Tooltip>
        )}
      </SelectOptionRoot>
    );
  }
);

SelectOption.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  secondaryText: PropTypes.string,
  renderIcon: PropTypes.func,
  isCheckbox: PropTypes.bool,
  BadgeProps: PropTypes.object,
  actionProps: PropTypes.object,
  CheckboxProps: PropTypes.object,
  isIndeterminate: PropTypes.bool,
  isSelected: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isFocused: PropTypes.bool,
  isLastSelected: PropTypes.bool,
  isPreSelectedItem: PropTypes.bool,
  isGroup: PropTypes.bool,
  isNested: PropTypes.bool,
  isGroupCollapsed: PropTypes.bool,
  showGroupCounter: PropTypes.bool
};

SelectOption.classes = classes;
SelectOption.displayName = 'SelectOption';

export default SelectOption;
